/**
 * Conversation Sidebar Component
 * 
 * Displays conversation history and bookmarks.
 */

import { createConversationListItem, createDateGroupHeader } from './conversation-list-item.js';
import { groupConversationsByDate } from './utils/format-utils.js';
import { Icons } from './utils/icons.js';
import { t } from '../../i18n.js';

/**
 * Create the conversation sidebar
 * @param {Object} options
 * @param {Object} options.state - AIAssistantState instance
 * @param {Function} options.onNewChat - Callback for new chat button
 * @param {Function} options.onSelectConversation - Callback when conversation is selected
 * @param {Function} options.onBookmark - Callback to toggle bookmark
 * @param {Function} options.onDelete - Callback to delete conversation
 * @param {Function} options.onClose - Callback to close sidebar
 * @returns {HTMLElement}
 */
export function createConversationSidebar({
  state,
  onNewChat,
  onSelectConversation,
  onBookmark,
  onDelete,
  onClose
}) {
  const sidebar = document.createElement('div');
  sidebar.className = 'ai-sidebar';
  
  // Header
  const header = document.createElement('div');
  header.className = 'ai-sidebar-header';
  
  const title = document.createElement('h3');
  title.className = 'ai-sidebar-title';
  title.textContent = t('ai_conversations_title');
  
  const closeBtn = document.createElement('button');
  closeBtn.className = 'ai-sidebar-close';
  closeBtn.innerHTML = Icons.close();
  closeBtn.onclick = onClose;
  
  header.appendChild(title);
  header.appendChild(closeBtn);
  
  // New chat button
  const newChatBtn = document.createElement('button');
  newChatBtn.className = 'ai-new-chat-btn';
  newChatBtn.innerHTML = `${Icons.plus()} ${t('ai_new_chat_btn')}`;
  newChatBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onNewChat();
  });
  
  // Content area
  const content = document.createElement('div');
  content.className = 'ai-sidebar-content';
  
  // Bookmarked section
  const bookmarkedSection = document.createElement('div');
  bookmarkedSection.className = 'ai-sidebar-section bookmarked';
  bookmarkedSection.id = 'ai-bookmarked-section';
  
  const bookmarkedHeader = document.createElement('div');
  bookmarkedHeader.className = 'ai-section-header';
  bookmarkedHeader.innerHTML = `${Icons.bookmarkFilled()} ${t('ai_bookmarked')}`;
  
  const bookmarkedList = document.createElement('div');
  bookmarkedList.className = 'ai-conversation-list bookmarked';
  bookmarkedList.id = 'ai-bookmarked-list';
  
  bookmarkedSection.appendChild(bookmarkedHeader);
  bookmarkedSection.appendChild(bookmarkedList);
  
  // All conversations section
  const allSection = document.createElement('div');
  allSection.className = 'ai-sidebar-section all';
  
  const allHeader = document.createElement('div');
  allHeader.className = 'ai-section-header';
  allHeader.innerHTML = `${Icons.chat()} ${t('ai_all_conversations')}`;
  
  const conversationList = document.createElement('div');
  conversationList.className = 'ai-conversation-list';
  conversationList.id = 'ai-conversation-list';
  
  allSection.appendChild(allHeader);
  allSection.appendChild(conversationList);
  
  content.appendChild(bookmarkedSection);
  content.appendChild(allSection);
  
  sidebar.appendChild(header);
  sidebar.appendChild(newChatBtn);
  sidebar.appendChild(content);
  
  // Render function
  function render() {
    renderBookmarkedConversations();
    renderConversations();
  }
  
  function renderBookmarkedConversations() {
    const bookmarked = state.bookmarkedConversations || [];
    const currentId = state.currentConversationId;
    
    bookmarkedList.innerHTML = '';
    
    if (bookmarked.length === 0) {
      bookmarkedSection.style.display = 'none';
      return;
    }
    
    bookmarkedSection.style.display = 'block';
    
    bookmarked.forEach(conversation => {
      const item = createConversationListItem({
        conversation,
        isActive: conversation._id === currentId,
        onClick: onSelectConversation,
        onBookmark,
        onDelete
      });
      bookmarkedList.appendChild(item);
    });
  }
  
  function renderConversations() {
    const conversations = state.conversations || [];
    const currentId = state.currentConversationId;
    const isLoading = state.isConversationsLoading();
    
    conversationList.innerHTML = '';
    
    if (isLoading) {
      conversationList.appendChild(createLoadingState());
      return;
    }
    
    if (conversations.length === 0) {
      conversationList.appendChild(createEmptyState());
      return;
    }
    
    // Group by date
    const groups = groupConversationsByDate(conversations);
    
    groups.forEach((convos, groupLabel) => {
      // Add group header
      conversationList.appendChild(createDateGroupHeader(groupLabel));
      
      // Add conversations
      convos.forEach(conversation => {
        const item = createConversationListItem({
          conversation,
          isActive: conversation._id === currentId,
          onClick: onSelectConversation,
          onBookmark,
          onDelete
        });
        conversationList.appendChild(item);
      });
    });
  }
  
  function createLoadingState() {
    const loading = document.createElement('div');
    loading.className = 'ai-sidebar-loading';
    loading.innerHTML = `
      <div class="ai-loading-spinner"></div>
      <span>${t('ai_loading_conversations')}</span>
    `;
    return loading;
  }
  
  function createEmptyState() {
    const empty = document.createElement('div');
    empty.className = 'ai-sidebar-empty';
    empty.innerHTML = `
      <div class="ai-empty-icon">${Icons.chat()}</div>
      <p>${t('ai_no_conversations')}</p>
      <p class="ai-empty-hint">${t('ai_start_chat_hint')}</p>
    `;
    return empty;
  }
  
  // Initial render
  render();
  
  // Public API
  sidebar.render = render;
  
  return sidebar;
}
